import type { BookableResourceRef } from "./bookable-resource";
import type { ResourceRequirementInput } from "./common";
import { ENVELOPE_DURATION_CAP_MINUTES, ENVELOPE_DURATION_INCREMENT_MINUTES } from "./envelope-duration";
import { PreconditionFailedError, SlotConflictError } from "./errors";
import type { Brand } from "./lib/brand";
import type { MemberId } from "./member-identity";
import type { ScheduleId } from "./schedule.model";
import { StepPlacement } from "./step-placement";
import { TimeRange } from "./time-range";
import type { ResourceAssignment } from "./visit.model";

// ============================================================
// Booking — the workflow BEFORE an appointment exists (ADR-0019)
//
// Everything here is transient. Step references survive into reservations;
// candidates have no lifecycle or FHIR vocabulary. What is being booked lives
// in the order set, what has been booked lives in the visit, and this is only
// the space between them.
//
// THE WORKFLOW: resolve the work → establish eligibility → match demand against
// supply → choose → accept → commit. This file is the first and third steps.
// ============================================================

/** Every actor whose calendar a booking can claim. */
type BookableResourceType = BookableResourceRef["type"];

/**
 * One concrete actor and the calendar it books against.
 *
 * Both halves are needed downstream: availability is searched by calendar, and
 * the visit reserves the ACTOR — so resolving a claim to a schedule alone would
 * force a second read to name what was actually booked.
 */
export interface ResourceInstance {
  resource: BookableResourceRef;
  scheduleId: ScheduleId;
  /** False excludes new bookings, but still identifies resources required by active work. */
  eligible?: boolean;
}

/**
 * One thing that has to be free for the full step reservation.
 *
 * `instances` identifies matching resources, with eligibility for new bookings.
 * Which eligible room or device gets booked is an availability
 * outcome, decided per candidate start ([[Scheduling Domain Model]] line 48:
 * "Scheduling automatically allocates any eligible available room").
 *
 * Reservation timing belongs to the step; clinical procedure durations do not
 * change how long any of its required resources are reserved.
 */
export interface ResourceClaim {
  resourceType: BookableResourceType;
  instances: ResourceInstance[];
}

/**
 * A group of procedures that run concurrently, reduced to what booking needs:
 * its authored placement, and what it holds while it runs.
 *
 * WHAT COUNTS AS ONE CLAIM IS THE RESOLVER'S ANSWER, not this file's. A step is
 * one patient, so concurrent procedures needing the same thing are ONE claim
 * whether that thing is a room ([[Scheduling Domain Model]] line 14) or a
 * device: two rooms would put the patient in both, and two stimulators would
 * demand a second machine for what is a second electrode site. Only an explicit
 * quantity asks for more than one. That is a fact about the requirements, which
 * have already been read by the time this is built.
 */
export interface WorkStep {
  /** Owning OrderSet step; absent for whole-visit and consultation claims. */
  stepId?: MemberId;
  placement: StepPlacement;
  claims: ResourceClaim[];
}

/**
 * One claim, positioned. `offset` is minutes from the start of the booking, so
 * a candidate start `T` puts this segment at `[T+offset, T+offset+duration]`.
 */
interface BookableSegment {
  /** Owning OrderSet step; absent for whole-visit and consultation claims. */
  stepId?: MemberId;
  resourceType: BookableResourceType;
  instances: readonly ResourceInstance[];
  requiredResources: readonly BookableResourceRef[];
  duration: number;
  offset: number;
}

/**
 * One calendar's free starts for one reservation length, as `$find` answered.
 *
 * Length matters: a calendar free for 15 minutes at 09:30 may not be free for
 * 30, so an instance is asked once per distinct duration its claims need.
 */
export interface CalendarFreeStarts {
  scheduleId: ScheduleId;
  durationMinutes: number;
  freeStarts: readonly string[];
}

/** One instance, seated: which actor, on which calendar, for exactly when. */
type PlacementIdentity = {
  [Kind in BookableResourceRef["type"]]: {
    resourceType: Kind;
    resourceId: Extract<BookableResourceRef, { type: Kind }>["id"];
  };
}[BookableResourceRef["type"]];
type Placement = PlacementIdentity & {
  stepId?: MemberId;
  scheduleId: ScheduleId;
  start: string;
  end: string;
};

function placementIdentity(resource: BookableResourceRef): PlacementIdentity {
  switch (resource.type) {
    case "Practitioner":
      return { resourceType: resource.type, resourceId: resource.id };
    case "Location":
      return { resourceType: resource.type, resourceId: resource.id };
    case "Device":
      return { resourceType: resource.type, resourceId: resource.id };
  }
}
function placementResource(placement: Placement): BookableResourceRef {
  switch (placement.resourceType) {
    case "Practitioner":
      return { type: placement.resourceType, id: placement.resourceId };
    case "Location":
      return { type: placement.resourceType, id: placement.resourceId };
    case "Device":
      return { type: placement.resourceType, id: placement.resourceId };
  }
}

/**
 * One bookable option — a start time with every claim seated.
 *
 * Never persisted, and it round-trips through an untrusted client, so it
 * carries placement and nothing clinical. What is being delivered is named by
 * reference at the call site.
 */
export class BookableCandidate {
  readonly start: string;
  readonly end: string;
  readonly placements: readonly Placement[];
  readonly #window: TimeRange;

  constructor(start: string, end: string, placements: readonly Placement[]) {
    this.#window = timeRange(start, end);
    if (placements.length === 0) {
      throw new PreconditionFailedError("A booking candidate must seat at least one resource");
    }
    this.start = start;
    this.end = end;
    this.placements = placements.map((placement) => ({ ...placement }));
  }

  /**
   * Turn this option into reservations, or refuse it.
   *
   * A candidate is advisory from the moment it is handed out: it travels to a
   * browser, waits on a human, and comes back, and in between someone else may
   * have taken the room. This is the one place that re-decides, and the one
   * place that says what a placement becomes — both were previously written out
   * longhand, differently, in each booking handler.
   *
   * It decides; it never writes. `facts` is read by the caller and passed in.
   *
   * Still a read followed by a write rather than a lock, so a simultaneous
   * booking can win the race. That was true of what it replaces too.
   */
  accept(facts: AcceptFacts): Reservation[] {
    if (this.#window.start < new Date(facts.now)) {
      throw new PreconditionFailedError(`Cannot book ${this.start}, which is already past`);
    }

    for (const placement of this.placements) {
      const conflict = facts.busy.find(
        (slot) =>
          slot.scheduleId === placement.scheduleId &&
          timeRange(slot.start, slot.end).overlaps(timeRange(placement.start, placement.end)),
      );
      if (conflict) {
        throw new SlotConflictError(
          `Schedule/${placement.scheduleId} already has a busy slot overlapping ${placement.start}–${placement.end}`,
        );
      }
    }

    return this.placements.map((placement) => ({
      ...(placement.stepId ? { stepId: placement.stepId } : {}),
      resource: placementResource(placement),
      calendarId: placement.scheduleId,
      window: { start: placement.start, end: placement.end },
    }));
  }
}

/** What `accept` needs read for it. Evidence, not a verdict. */
export interface AcceptFacts {
  /** Now, as an instant. Passed in so the decision stays pure and testable. */
  now: string;
  /** Busy intervals on any calendar — entries for calendars this candidate never claimed are ignored. */
  busy: ReadonlyArray<{ scheduleId: ScheduleId; start: string; end: string }>;
}

/** A seated placement, in the shape a Visit is built from. */
export interface Reservation {
  /** Owning OrderSet step; absent for whole-visit and consultation claims. */
  stepId?: MemberId;
  resource: BookableResourceRef;
  calendarId: ScheduleId;
  window: { start: string; end: string };
}

export interface BookingConflict {
  stepId?: MemberId;
  resource: BookableResourceRef | { type: BookableResourceType; id?: never };
  calendarId?: ScheduleId;
  window: { start: string; end: string };
  collision:
    | { kind: "no-eligible-instance" }
    | { kind: "busy"; slotId?: BookingSlotResourceId; window: { start: string; end: string } }
    | { kind: "proposal"; stepId?: MemberId; assignmentId?: MemberId; window: { start: string; end: string } };
}

/** Advisory only: a later submit must re-check current facts. */
export interface BookingCheck {
  bookable: boolean;
  window: { start: string; end: string };
  /** Complete only when bookable; new claims have no persisted member identity yet. */
  assignments: Array<Reservation & { id?: MemberId }>;
  conflicts: BookingConflict[];
}

interface CandidateOptions {
  /**
   * The booking grid in minutes — the clinic offers starts every N minutes,
   * whatever the reservation length. Applied to the candidate's start only;
   * later segments fall where the work puts them.
   */
  cadenceMinutes?: number;
}

const MS_PER_MINUTE = 60_000;

type BusySlot = { id?: BookingSlotResourceId; scheduleId: ScheduleId; start: string; end: string };

/**
 * What is being booked, expressed as calendar claims.
 *
 * A Consultation, a standalone Protocol Visit and a curated session all reduce
 * to this, which is the point: they differ in where their steps come from, and
 * in nothing after that. One availability search serves all three.
 */
export class BookableWork {
  readonly duration: number;
  readonly segments: readonly BookableSegment[];

  private constructor(duration: number, segments: readonly BookableSegment[]) {
    this.duration = duration;
    this.segments = segments;
  }

  /**
   * Position every claim at its step's authored offset.
   *
   * The anchor is a single claim over the whole span — a clinician commits to
   * the entire booking, not to the steps that happen to need them.
   */
  static createProtocolWork(
    anchor: ResourceInstance,
    steps: readonly WorkStep[],
    envelopeMinutes: number,
    options: { allowUnseatable?: boolean } = {},
  ): BookableWork {
    BookableWork.assertProtocolDuration(envelopeMinutes);
    if (steps.some((step) => !step.placement.fitsWithin(envelopeMinutes))) {
      throw new PreconditionFailedError("The protocol envelope must contain all work steps");
    }
    return BookableWork.positionSteps(anchor, steps, envelopeMinutes, options.allowUnseatable);
  }

  static createConsultationWork(
    anchor: ResourceInstance,
    durationMinutes: number,
    requirements: readonly ResourceClaim[],
  ): BookableWork {
    if (!Number.isFinite(durationMinutes) || durationMinutes <= 0) {
      throw new PreconditionFailedError("A consultation duration must be finite and positive");
    }
    const placement = new StepPlacement({ offsetMinutes: 0, durationMinutes });
    return BookableWork.positionSteps(anchor, [{ placement, claims: [...requirements] }], durationMinutes);
  }

  private static assertProtocolDuration(minutes: number): void {
    if (
      !Number.isFinite(minutes) ||
      minutes <= 0 ||
      minutes % ENVELOPE_DURATION_INCREMENT_MINUTES !== 0 ||
      minutes > ENVELOPE_DURATION_CAP_MINUTES
    ) {
      throw new PreconditionFailedError(
        `A booking duration must be a positive multiple of 5 no greater than ${ENVELOPE_DURATION_CAP_MINUTES} minutes`,
      );
    }
  }

  private static positionSteps(
    anchor: ResourceInstance,
    steps: readonly WorkStep[],
    total: number,
    allowUnseatable = false,
  ): BookableWork {
    const segments: BookableSegment[] = [
      {
        resourceType: "Practitioner",
        instances: [{ ...anchor }],
        requiredResources: [anchor.resource],
        duration: total,
        offset: 0,
      },
    ];

    for (const step of steps) {
      for (const claim of step.claims) {
        const eligible = claim.instances.filter((instance) => instance.eligible !== false);
        if (eligible.length === 0 && !allowUnseatable) {
          throw new PreconditionFailedError(`A ${claim.resourceType} claim requires at least one eligible instance`);
        }
        segments.push({
          ...(step.stepId ? { stepId: step.stepId } : {}),
          resourceType: claim.resourceType,
          instances: eligible.map((instance) => ({ ...instance })),
          requiredResources: claim.instances.map((instance) => instance.resource),
          duration: step.placement.durationMinutes,
          offset: step.placement.offsetMinutes,
        });
      }
    }

    return new BookableWork(total, segments);
  }

  /**
   * Every start at which this work can actually be booked.
   *
   * The caller has run `$find` once per (instance, claim duration) over the
   * search window and hands the answers back; this is the in-memory pass that
   * turns them into options. Pure — the reading is the caller's, the deciding
   * is here.
   *
   * THE ANCHOR DRIVES THE GRID. Its free starts are the only starts considered,
   * because a booking cannot happen without the clinician, and every other
   * claim is then checked at its own offset. Walking every calendar's starts
   * instead would propose times the anchor was never free for.
   */
  candidatesFrom(availability: readonly CalendarFreeStarts[], options: CandidateOptions = {}): BookableCandidate[] {
    const free = indexFreeStarts(availability);
    const [anchor, ...claims] = this.segments;
    if (!anchor) return [];

    const candidates: BookableCandidate[] = [];
    for (const start of free.startsFor(anchor.instances[0]!.scheduleId, anchor.duration)) {
      if (options.cadenceMinutes && !isOnGrid(start, options.cadenceMinutes)) continue;
      const placements = this.seat(start, anchor, claims, free);
      if (placements) {
        candidates.push(new BookableCandidate(start, addMinutes(start, this.duration), placements));
      }
    }
    return candidates;
  }

  /** Query and check the same effective reservations, including retained resources. */
  planAt(start: string, facts: { existing: readonly ResourceAssignment[]; unchangedStepIds?: readonly MemberId[] }) {
    const used = new Set<MemberId>();
    const unchanged = new Set(facts.unchangedStepIds ?? []);
    const retained = facts.existing.filter((assignment) => assignment.stepId && unchanged.has(assignment.stepId));
    for (const assignment of retained) used.add(assignment.id);
    const requests = this.segments
      .filter((segment) => !segment.stepId || !unchanged.has(segment.stepId))
      .map((segment) => {
        const window = {
          start: addMinutes(start, segment.offset),
          end: addMinutes(start, segment.offset + segment.duration),
        };
        const previous = facts.existing.find(
          (assignment) =>
            !used.has(assignment.id) &&
            assignment.stepId === segment.stepId &&
            assignment.resource.type === segment.resourceType &&
            (segment.resourceType === "Practitioner" ||
              segment.instances.some(
                (instance) =>
                  instance.resource.id === assignment.resource.id && instance.scheduleId === assignment.calendarId,
              )),
        );
        if (previous) used.add(previous.id);
        return { segment, window, previous };
      });
    for (const previous of retained) {
      // Keep the assigned instance, but derive active work's reservation from
      // its step. Resources needed only by delivered work keep their recorded window.
      const step = this.segments.find(
        (segment) =>
          segment.stepId === previous.stepId &&
          segment.requiredResources.some(
            (resource) => resource.type === previous.resource.type && resource.id === previous.resource.id,
          ),
      );
      requests.push({
        previous,
        window: step
          ? { start: addMinutes(start, step.offset), end: addMinutes(start, step.offset + step.duration) }
          : previous.window,
        segment: {
          stepId: previous.stepId,
          resourceType: previous.resource.type,
          instances: [{ resource: previous.resource, scheduleId: previous.calendarId }],
          requiredResources: [previous.resource],
          offset: 0,
          duration: 0,
        },
      });
    }
    const windows = requests.flatMap(({ segment, window, previous }) =>
      (previous ? [previous.calendarId] : segment.instances.map((instance) => instance.scheduleId)).map(
        (scheduleId) => ({ scheduleId, ...window }),
      ),
    );
    return {
      windows,
      check: (busy: readonly BusySlot[]): BookingCheck => {
        const assignments: BookingCheck["assignments"] = [];
        const conflicts: BookingConflict[] = [];
        const busySlots = [
          ...new Map(
            busy.map((slot) => [JSON.stringify([slot.id, slot.scheduleId, slot.start, slot.end]), slot]),
          ).values(),
        ];
        // Reserve existing instances first, independent of the proposal's ordering.
        const held: BookingCheck["assignments"] = requests.flatMap(({ previous, window }) =>
          previous ? [{ ...previous, window }] : [],
        );
        for (const { segment, window, previous } of requests) {
          const choices: BookingCheck["assignments"] = previous
            ? [{ ...previous, window }]
            : segment.instances.map((instance) => ({
                ...(segment.stepId ? { stepId: segment.stepId } : {}),
                resource: instance.resource,
                calendarId: instance.scheduleId,
                window,
              }));
          if (choices.length === 0)
            conflicts.push({
              stepId: segment.stepId,
              resource: { type: segment.resourceType },
              window,
              collision: { kind: "no-eligible-instance" },
            });
          const collisions = (choice: Reservation & { id?: MemberId }): BookingConflict[] => {
            const busy: BookingConflict[] = busySlots
              .filter(
                (slot) =>
                  slot.scheduleId === choice.calendarId &&
                  timeRange(slot.start, slot.end).overlaps(timeRange(window.start, window.end)),
              )
              .map((slot) => ({
                ...choice,
                collision: {
                  kind: "busy",
                  ...(slot.id ? { slotId: slot.id } : {}),
                  window: { start: slot.start, end: slot.end },
                },
              }));
            return [
              ...busy,
              ...held
                .filter(
                  (other) =>
                    !(choice.id && other.id === choice.id) &&
                    other.calendarId === choice.calendarId &&
                    timeRange(other.window.start, other.window.end).overlaps(timeRange(window.start, window.end)),
                )
                .map(
                  (other): BookingConflict => ({
                    ...choice,
                    collision: { kind: "proposal", stepId: other.stepId, assignmentId: other.id, window: other.window },
                  }),
                ),
            ];
          };
          const chosen = choices.find((choice) => collisions(choice).length === 0);
          if (chosen) {
            assignments.push(chosen);
            if (!previous) held.push(chosen);
          } else conflicts.push(...choices.flatMap(collisions));
        }
        return {
          bookable: conflicts.length === 0,
          window: { start, end: addMinutes(start, this.duration) },
          assignments,
          conflicts,
        };
      },
    };
  }

  /** Check named windows without querying calendars. */
  checkAt(
    start: string,
    facts: {
      busy: readonly BusySlot[];
      existing: readonly ResourceAssignment[];
      unchangedStepIds?: readonly MemberId[];
    },
  ): BookingCheck {
    return this.planAt(start, facts).check(facts.busy);
  }

  /**
   * Seat every claim from one start, or refuse.
   *
   * Returns null rather than throwing: an unseatable start is the NORMAL result
   * across a month of calendar, not an error.
   */
  private seat(
    start: string,
    anchor: BookableSegment,
    claims: readonly BookableSegment[],
    free: FreeStartIndex,
  ): Placement[] | null {
    const held: Placement[] = [
      {
        ...placementIdentity(anchor.instances[0]!.resource),
        scheduleId: anchor.instances[0]!.scheduleId,
        start,
        end: addMinutes(start, anchor.duration),
      },
    ];

    for (const claim of claims) {
      const from = addMinutes(start, claim.offset);
      const until = addMinutes(from, claim.duration);

      // One instance cannot be in two places at once. Concurrent claims
      // therefore take DIFFERENT instances, while a room used by two steps that
      // do not overlap may be the same room twice.
      const requested = new TimeRange(new Date(from), new Date(until));
      const chosen = claim.instances.find(
        (instance) =>
          free.has(instance.scheduleId, claim.duration, from) &&
          !held.some(
            (placement) =>
              placement.resourceId === instance.resource.id &&
              timeRange(placement.start, placement.end).overlaps(requested),
          ),
      );
      if (!chosen) return null;

      held.push({
        ...(claim.stepId ? { stepId: claim.stepId } : {}),
        ...placementIdentity(chosen.resource),
        scheduleId: chosen.scheduleId,
        start: from,
        end: until,
      });
    }

    return held;
  }
}

// ── internals ─────────────────────────────────────────────────

function timeRange(start: string, end: string): TimeRange {
  return new TimeRange(new Date(start), new Date(end));
}

interface FreeStartIndex {
  startsFor(scheduleId: ScheduleId, durationMinutes: number): readonly string[];
  has(scheduleId: ScheduleId, durationMinutes: number, start: string): boolean;
}

/** The (calendar, duration) keying is private: callers hand back what they were asked. */
function indexFreeStarts(availability: readonly CalendarFreeStarts[]): FreeStartIndex {
  const byKey = new Map<string, Set<string>>();
  const ordered = new Map<string, readonly string[]>();
  for (const answer of availability) {
    const key = `${answer.scheduleId}|${answer.durationMinutes}`;
    byKey.set(key, new Set(answer.freeStarts));
    ordered.set(key, answer.freeStarts);
  }
  return {
    startsFor: (scheduleId, durationMinutes) => ordered.get(`${scheduleId}|${durationMinutes}`) ?? [],
    has: (scheduleId, durationMinutes, start) => byKey.get(`${scheduleId}|${durationMinutes}`)?.has(start) ?? false,
  };
}

function isOnGrid(iso: string, gridMinutes: number): boolean {
  return new Date(iso).getUTCMinutes() % gridMinutes === 0;
}

function addMinutes(iso: string, minutes: number): string {
  return new Date(new Date(iso).getTime() + minutes * MS_PER_MINUTE).toISOString();
}

// ── Building a step's claims ──────────────────────────────────

/** The resources one procedure declares it needs. */
export interface ProcedureOccurrence {
  resourceRequirements?: ResourceRequirementInput[];
}

/**
 * Reduce one step's concurrent procedures to the claims booking must satisfy.
 *
 * EVERYTHING COALESCES, because a step is ONE PATIENT.
 *
 * A ROOM: concurrent procedures needing the same room category "coalesce into one
 * concrete Location reservation held for that step's duration rather than
 * consuming multiple rooms" ([[Scheduling Domain Model]] line 14) — one patient is
 * in one room, and they are in it for the whole step, not just the procedure that
 * asked. Quantity does not multiply a room for the same reason.
 *
 * A DEVICE is also reserved for the full step. Concurrent procedures asking
 * for the same device kind are placements on ONE unit — six NINM occurrences in a
 * step are six electrode sites on one stimulator, not six stimulators — so they
 * share a claim. Quantity still multiplies here: asking for two of a kind is a statement
 * that the procedure needs two machines at once, and two claims is what forces
 * them onto distinct instances later.
 *
 * `eligibleFor` is handed in already resolved: which rooms carry a category is a
 * search, and this is a rule.
 */
export function toWorkStep(
  occurrences: readonly ProcedureOccurrence[],
  eligibleFor: (requirement: ResourceRequirementInput) => ResourceInstance[],
  placement: StepPlacement,
): WorkStep {
  // One entry per distinct requirement in the step, in first-seen order.
  const shared = new Map<
    string,
    { resourceType: BookableResourceType; instances: ResourceInstance[]; units: number }
  >();

  for (const occurrence of occurrences) {
    for (const requirement of occurrence.resourceRequirements ?? []) {
      const key = `${requirement.resourceType}|${requirementKey(requirement)}`;
      const isRoom = requirement.resourceType === "Location";
      const existing = shared.get(key);
      const units = isRoom ? 1 : Math.max(1, requirement.quantity);

      if (!existing) {
        shared.set(key, {
          resourceType: requirement.resourceType,
          instances: eligibleFor(requirement),
          units,
        });
        continue;
      }
      existing.units = Math.max(existing.units, units);
    }
  }

  const claims: ResourceClaim[] = [];
  for (const { resourceType, instances, units } of shared.values()) {
    for (let unit = 0; unit < units; unit++) {
      claims.push({ resourceType, instances });
    }
  }

  return { placement, claims };
}

/** What makes two requirements "the same room" — a named resource, or a category. */
function requirementKey(requirement: ResourceRequirementInput): string {
  if (requirement.reference) return `ref:${requirement.reference}`;
  const code = requirement.category?.coding?.[0]?.code;
  if (!code) {
    throw new PreconditionFailedError("A resource requirement must name a reference or a category code");
  }
  return `cat:${code}`;
}

/** Exact busy Slot locator, used only to report a collision. */
export type BookingSlotResourceId = Brand<string, "BookingSlotResourceId">;
export function asBookingSlotResourceId(value: string): BookingSlotResourceId {
  return value as BookingSlotResourceId;
}
